import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Html5Qrcode } from 'html5-qrcode';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';

const Scanner: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [permissionStatus, setPermissionStatus] = useState<string>('prompt');
  const [isComponentMounted, setIsComponentMounted] = useState<boolean>(true);
  const [isProcessingQR, setIsProcessingQR] = useState<boolean>(false);
  const [hasNavigated, setHasNavigated] = useState<boolean>(false);
  const [qrDetectedTime, setQrDetectedTime] = useState<number | null>(null);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  // Removed processingTime - not needed
  const processingRef = useRef<boolean>(false);
  const navigatedRef = useRef<boolean>(false);
  const scannerDestroyedRef = useRef<boolean>(false);
  const recoveryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const globalFailsafeRef = useRef<NodeJS.Timeout | null>(null);
  const initializingRef = useRef<boolean>(false);  // Prevent duplicate scanner initialization
  const [torchSupported, setTorchSupported] = useState<boolean>(false);
  const [torchOn, setTorchOn] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = "qr-reader-container-unique";
  const preferredBackCameraIdRef = useRef<string | null>(null);
  const { theme } = useTheme();
  const { clearVendorContext } = useCart();
  
  // CRITICAL: Reset refs on mount and unmount to prevent permanent blocking
  useEffect(() => {
    console.log("Scanner mounted - resetting refs to prevent blocking");
    processingRef.current = false;
    navigatedRef.current = false;
    scannerDestroyedRef.current = false;
    initializingRef.current = false;
    
    return () => {
      // Reset when scanner is unmounted - CRITICAL FIX
      processingRef.current = false;
      navigatedRef.current = false;
      scannerDestroyedRef.current = false;
      initializingRef.current = false;
      console.log("Scanner unmounted - reset blocking refs");
    };
  }, []);
  
  // Simple cleanup on component unmount
  useEffect(() => {
    return () => {
      if (globalFailsafeRef.current) {
        clearTimeout(globalFailsafeRef.current);
        globalFailsafeRef.current = null;
      }
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
        processingTimerRef.current = null;
      }
    };
  }, []);
  
  // Auto-recovery mechanism - reset flags if stuck for too long
  useEffect(() => {
    const checkAndRecover = () => {
      const now = Date.now();
      const isStuck = (processingRef.current || navigatedRef.current) && 
                     qrDetectedTime && 
                     (now - qrDetectedTime > 8000); // 8 seconds (reduced from 10)
      
      if (isStuck) {
        console.error("AUTO-RECOVERY: Scanner stuck for >8s, forcing reset", {
          processingRef: processingRef.current,
          navigatedRef: navigatedRef.current,
          timeStuck: now - qrDetectedTime,
          qrDetectedTime: new Date(qrDetectedTime).toISOString()
        });
        
        setIsBlocked(true);
        setError("Scanner got stuck. Auto-resetting...");
        
        // IMMEDIATE force reset - don't wait for user
        setTimeout(() => {
          console.warn("AUTO-RECOVERY: Force resetting scanner NOW");
          resetScanner();
        }, 1000); // Only wait 1 second
      }
    };
    
    const recoveryInterval = setInterval(checkAndRecover, 1000); // Check every 1 second (more frequent)
    
    return () => {
      clearInterval(recoveryInterval);
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
      }
    };
  }, [qrDetectedTime]);
  
  // IMPORTANT: Clear vendor context when Scanner page mounts
  // This prevents getting stuck in vendor menu when navigating here
  useEffect(() => {
    setIsComponentMounted(true);
    
    const resetVendorContext = async () => {
      console.log('Scanner page mounted - FORCE CLEARING vendor context');
      
      // AGGRESSIVE RESET: Clear all blocking flags on mount
      setIsProcessingQR(false);
      setHasNavigated(false);
      setIsBlocked(false);
      setQrDetectedTime(null);
      processingRef.current = false;
      navigatedRef.current = false;
      scannerDestroyedRef.current = false;
      initializingRef.current = false;
      
      // Clear immediately from localStorage
      localStorage.removeItem('vendorId');
      localStorage.removeItem('qrCode');
      localStorage.removeItem('previousPath');
      
      // Then clear through context
      await clearVendorContext();
      
      // Verify it's cleared
      console.log('Scanner page context cleared, localStorage state:', {
        vendorId: localStorage.getItem('vendorId'),
        qrCode: localStorage.getItem('qrCode')
      });
    };
    
    // Execute immediately
    resetVendorContext();

    // Cleanup function
    return () => {
      setIsComponentMounted(false);
      setIsProcessingQR(false);
      setHasNavigated(false);
      setIsBlocked(false);
      setQrDetectedTime(null);
      // Removed setProcessingTime - not needed
      processingRef.current = false;
      navigatedRef.current = false;
      scannerDestroyedRef.current = false;
      initializingRef.current = false;
      
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
      }
      
      if (processingTimerRef.current) {
        clearInterval(processingTimerRef.current);
      }
      
      if (globalFailsafeRef.current) {
        clearTimeout(globalFailsafeRef.current);
        globalFailsafeRef.current = null;
      }
      
      console.log('Scanner component unmounting - cleaning up scanner and camera');
      
      // AGGRESSIVE: Stop all camera streams
      const allVideoElements = document.querySelectorAll('video');
      allVideoElements.forEach(video => {
        if (video.srcObject && video.srcObject instanceof MediaStream) {
          const stream = video.srcObject as MediaStream;
          stream.getTracks().forEach(track => {
            track.stop();
            console.log("Stopped video track during unmount");
          });
          video.srcObject = null;
        }
      });
      
      if (scannerRef.current) {
        try {
          scannerRef.current.stop();
        } catch (error) {
          console.error("Error stopping scanner during unmount:", error);
        } finally {
          scannerRef.current = null;
        }
      }
      setIsScanning(false);
      console.log("Scanner cleanup completed - camera should be off");
    };
  }, [clearVendorContext]);

  // Process QR code and navigate to menu
  const handleNavigation = async (scannedValue: string) => {
    console.log("=== NAVIGATION START ===", { scannedValue });
    
    if (!isComponentMounted) {
      return;
    }
    
    // AGGRESSIVE: Stop all camera streams before navigation
    const allVideoElements = document.querySelectorAll('video');
    allVideoElements.forEach(video => {
      if (video.srcObject && video.srcObject instanceof MediaStream) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach(track => {
          track.stop();
          console.log("Stopped video track before navigation");
        });
        video.srcObject = null;
      }
    });
    
    setError(null);
    setIsProcessingQR(true);

    // Declare variables at the top for timeout function access
    let code: string = '';

    try {
      // Step 1: Clear vendor context
      console.log("Step 1: Clearing vendor context...");
      clearVendorContext();
      console.log("Step 1: Vendor context cleared");

      // Step 2: Extract QR code from URL
      if (scannedValue.includes('qrCode=')) {
        // Handle URL format: http://localhost:8081/sogweb/menu?vendorId=2&qrCode=QR-ABC123
        const urlParams = new URLSearchParams(scannedValue.split('?')[1]);
        code = urlParams.get('qrCode') || '';
        const vendorIdFromUrl = urlParams.get('vendorId') || '';
        console.log("Step 2: Extracted from URL params - code:", code, "vendorId:", vendorIdFromUrl);
      } else {
        // Handle direct QR code: QR-ABC123
        code = scannedValue;
        console.log("Step 2: Extracted code from URL:", code);
      }

      if (!code) {
        throw new Error("No QR code found in scanned value");
      }

      // Step 3: Make API request to get vendor info
      console.log("Step 3: Making API request for QR code:", code);
      const response = await api.get<{
        vendorId: number;
        vendorName: string;
        tableId?: number;
        tableName?: string;
        qrCode: string;
        categories: Array<any>;
      }>(`/qr/${code}/menu`);
      console.log("Step 3: API response received:", {
        status: response.status,
        vendorId: response.data?.vendorId,
        vendorName: response.data?.vendorName,
        tableId: response.data?.tableId
      });

      if (response.data && response.data.vendorId) {
        const vendorIdFromApi = response.data.vendorId;
        
        console.log("Step 4: Navigation successful, going to menu:", vendorIdFromApi);
        
        // CRITICAL: Completely destroy scanner BEFORE navigation
        console.log("Step 4: DESTROYING SCANNER before navigation");
        
        // Set blocking flags immediately
        scannerDestroyedRef.current = true;
        setHasNavigated(true);
        setIsProcessingQR(false);
        
        // Force destroy scanner and remove DOM element
        if (scannerRef.current) {
          try {
            console.log("FORCE DESTROYING scanner and DOM element...");
            scannerRef.current.stop();
            scannerRef.current = null;
            
            // Remove DOM element completely
            const container = document.getElementById(scannerContainerId);
            if (container) {
              container.remove();
              console.log("Scanner DOM element REMOVED");
            }
            
            // AGGRESSIVE: Remove ALL scanner-related DOM elements
            const allScannerElements = document.querySelectorAll('[id*="qr"], [class*="qr"], [id*="scanner"], [class*="scanner"]');
            allScannerElements.forEach(element => {
              if (element.id !== scannerContainerId) { // Don't double-remove the main container
                console.log("Removing additional scanner element:", element.id || element.className);
                element.remove();
              }
            });
            
            // Stop any active video elements and their streams
            const videoElements = document.querySelectorAll('video');
            videoElements.forEach(video => {
              if (video.srcObject && video.srcObject instanceof MediaStream) {
                video.srcObject.getTracks().forEach(track => {
                  track.stop();
                  console.log("Video track stopped");
                });
                video.srcObject = null;
              }
              video.pause();
              video.remove();
            });
            
          } catch (error) {
            console.error("Error destroying scanner:", error);
          }
        }
        
        // Clear the recovery timeout since navigation is working
        if (recoveryTimeoutRef.current) {
          clearTimeout(recoveryTimeoutRef.current);
          recoveryTimeoutRef.current = null;
          console.log("Step 4: Recovery timeout cleared");
        }
        
        // Clear the global fail-safe since navigation is working
        if (globalFailsafeRef.current) {
          clearTimeout(globalFailsafeRef.current);
          globalFailsafeRef.current = null;
          console.log("Step 4: Global fail-safe cleared");
        }
        
        // Step 5: Navigate to menu page using React Router (smoother transition)
        const navigationUrl = `/menu/${vendorIdFromApi}?qr=${code}`;
        console.log("Navigating to:", navigationUrl);
        
        // Use React Router navigate for smoother transition
        navigate(navigationUrl);
        
      } else {
        throw new Error(`QR code not found: ${code}`);
      }
    } catch (error) {
      console.error("Navigation error:", error);
      setError(`Failed to process QR code: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Reset flags on error
      setIsProcessingQR(false);
      setHasNavigated(false);
      setIsBlocked(false);
      setQrDetectedTime(null);
      processingRef.current = false;
      navigatedRef.current = false;
      scannerDestroyedRef.current = false;
    }
  };

  // Reset scanner state and restart
  const resetScanner = () => {
    console.log("Manually resetting scanner state");
    
    // Clear all timeouts
    if (recoveryTimeoutRef.current) {
      clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }
    
    if (processingTimerRef.current) {
      clearInterval(processingTimerRef.current);
      processingTimerRef.current = null;
    }
    
    if (globalFailsafeRef.current) {
      clearTimeout(globalFailsafeRef.current);
      globalFailsafeRef.current = null;
    }
    
    // Reset all flags and state
    setIsProcessingQR(false);
    setHasNavigated(false);
    setIsBlocked(false);
    setQrDetectedTime(null);
    // Removed setProcessingTime - not needed
    processingRef.current = false;
    navigatedRef.current = false;
    scannerDestroyedRef.current = false;
    initializingRef.current = false;  // Reset scanner initialization flag
    
    // Clear any errors
    setError(null);
    
    // Clear and restart scanner
    if (scannerRef.current) {
      try {
        scannerRef.current.stop();
        scannerRef.current = null;
      } catch (error) {
        console.error("Error stopping scanner during reset:", error);
      }
    }
    
    // Re-initialize scanner
    setTimeout(async () => {
      if (isComponentMounted) {
        await initializeScanner();
      }
    }, 500);
  };

  // PHONEPE-STYLE: Direct camera access without permission UI
  const checkCameraPermission = async () => {
    if (!isComponentMounted) return 'denied';
    
    try {
      // Check if camera API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('❌ Camera API not available');
        if (isComponentMounted) {
          setPermissionStatus('denied');
          setError('Camera not supported on this device or browser');
        }
        return 'denied';
      }

      // DIRECT APPROACH: Get camera immediately without showing UI
      console.log('🚀 Requesting camera access directly...');
      
      const { cameraId, facingMode } = await getBestAvailableCamera();
      
      // Test camera access with the selected camera
      const testConstraints = cameraId 
        ? { video: { deviceId: { exact: cameraId } } }
        : { video: { facingMode: facingMode || 'environment' } };

      const stream = await navigator.mediaDevices.getUserMedia(testConstraints);
      
      // Ensure stream is valid
      if (!stream || !stream.getVideoTracks || stream.getVideoTracks().length === 0) {
        throw new Error('No video tracks available in stream');
      }

      // Stop the test stream immediately (scanner will create its own)
      stream.getTracks().forEach(track => {
        track.stop();
        console.log('🔄 Test camera track stopped:', track.kind);
      });

      // Wait for camera to be fully released
      await new Promise(resolve => setTimeout(resolve, 200));

      if (isComponentMounted) {
        setPermissionStatus('granted');
        console.log('✅ Camera access granted - proceeding to scanner');
      }
      return 'granted';
      
    } catch (error) {
      console.error("❌ Camera access failed:", error);
      if (isComponentMounted) {
        setPermissionStatus('denied');
        const err = error as Error;
        if (err?.name === 'NotAllowedError') {
          setError('Camera access denied. Please enable camera permissions in your browser and refresh the page.');
        } else if (err?.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else if (err?.name === 'NotReadableError') {
          setError('Camera is already in use by another application.');
        } else {
          setError('Failed to access camera. Please check your camera permissions and refresh the page.');
        }
      }
      return 'denied';
    }
  };

  // PHONEPE-STYLE: Get best available camera with comprehensive fallback
  const getBestAvailableCamera = async (): Promise<{cameraId?: string, facingMode?: string, cameraInfo: string}> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      console.log(`📱 Found ${videoDevices.length} camera(s):`, videoDevices.map(d => ({ 
        id: d.deviceId.substring(0, 8) + '...', 
        label: d.label || 'Unknown Camera' 
      })));

      if (videoDevices.length === 0) {
        throw new Error('No cameras available on this device');
      }

      // PRIORITY 1: Find back/rear camera by label
      const backCameraByLabel = videoDevices.find(device => {
        const label = device.label.toLowerCase();
        return label.includes('back') || label.includes('rear') || 
               label.includes('environment') || label.includes('facing back');
      });
      
      if (backCameraByLabel?.deviceId) {
        console.log('✅ Found back camera by label:', backCameraByLabel.label);
        return { 
          cameraId: backCameraByLabel.deviceId, 
          cameraInfo: `Back camera: ${backCameraByLabel.label}` 
        };
      }

      // PRIORITY 2: Try environment facing mode (works on most mobile devices)
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: { exact: 'environment' } } 
        });
        testStream.getTracks().forEach(track => track.stop());
        console.log('✅ Environment facing mode available');
        return { 
          facingMode: 'environment', 
          cameraInfo: 'Back camera via facingMode: environment' 
        };
      } catch (envError) {
        console.log('⚠️ Environment facing mode not available:', envError instanceof Error ? envError.message : 'Unknown error');
      }

      // PRIORITY 3: Use heuristics (last camera often back on mobile)
      if (videoDevices.length > 1) {
        const lastCamera = videoDevices[videoDevices.length - 1];
        console.log('⚠️ Using last camera as back camera heuristic:', lastCamera.label);
        return { 
          cameraId: lastCamera.deviceId, 
          cameraInfo: `Fallback to last camera: ${lastCamera.label}` 
        };
      }

      // PRIORITY 4: Use any available camera (fallback)
      const firstCamera = videoDevices[0];
      console.log('⚠️ Using first available camera as final fallback:', firstCamera.label);
      return { 
        cameraId: firstCamera.deviceId, 
        cameraInfo: `Any available camera: ${firstCamera.label}` 
      };

    } catch (error) {
      console.error('❌ Camera detection failed:', error);
      throw error;
    }
  };

  // PHONEPE-STYLE: Direct scanner initialization without any UI
  const initializeScanner = async () => {
    // Check if scanner was already destroyed
    if (scannerDestroyedRef.current) {
      console.log("Scanner was destroyed, preventing re-initialization");
      return;
    }

    // Prevent duplicate initialization
    if (initializingRef.current) {
      console.log("Scanner already initializing, preventing duplicate");
      return;
    }
    
    const containerElement = document.getElementById(scannerContainerId);
    if (!containerElement || !isComponentMounted) {
      console.log("Scanner container not ready or component unmounted");
      return;
    }

    // Set initializing flag
    initializingRef.current = true;
    console.log("🚀 Starting direct PhonePe-style scanner...");

    // Clear any existing scanner
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch (error) {
        console.error("Error stopping existing scanner:", error);
      }
    }

    try {
      // Get the best available camera
      const { cameraId, facingMode, cameraInfo } = await getBestAvailableCamera();
      console.log('📸 Selected camera:', cameraInfo);

      // Store preferred camera for flip functionality
      if (cameraId) {
        preferredBackCameraIdRef.current = cameraId;
      }

      // Create Html5Qrcode instance directly (no UI)
      const html5QrCode = new Html5Qrcode(scannerContainerId);
      scannerRef.current = html5QrCode;

      // Configuration for camera
      const cameraConfig = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1,
        disableFlip: true,
        ...(cameraId 
          ? { deviceId: { exact: cameraId } } 
          : { facingMode: { ideal: facingMode || 'environment' } }
        )
      };

      console.log('🎥 Starting camera with config:', cameraConfig);

      // Start scanning directly
      await html5QrCode.start(
        cameraId || { facingMode: facingMode || 'environment' },
        cameraConfig,
        (decodedText) => {
          // Simple blocking to prevent multiple scans
          if (!isComponentMounted || processingRef.current || navigatedRef.current) {
            return;
          }
          
          console.log("🎯 QR Code detected:", decodedText);
          
          // Set flags to prevent duplicate processing
          processingRef.current = true;
          navigatedRef.current = true;
          setIsProcessingQR(true);
          
          // Clean up scanner immediately
          if (scannerRef.current) {
            scannerRef.current.stop().catch(() => {});
            scannerRef.current = null;
          }
          
          // Navigate directly without delays
          handleNavigation(decodedText);
        },
        (errorMessage) => {
          // Filter out common expected errors that occur during normal scanning
          const isExpectedError = errorMessage.includes('No barcode or QR code detected') ||
                                  errorMessage.includes('No MultiFormat Readers were able to detect the code') ||
                                  errorMessage.includes('NotReadableError') ||
                                  errorMessage.includes('QR code parse error') ||
                                  errorMessage.includes('NotFoundException') ||
                                  errorMessage.includes('FormatException') ||
                                  errorMessage.includes('ChecksumException');
          
          // Only log serious errors
          if (!isExpectedError && scannerRef.current && isComponentMounted) {
            if (!errorMessage.includes('TypeError')) {
              console.warn("QR scanner error:", errorMessage);
            }
          }
        }
      );
      
      if (isComponentMounted) {
        setIsScanning(true);
        console.log("✅ Direct PhonePe-style scanner started successfully");
        initializingRef.current = false;

        // Detect torch capability
        setTimeout(() => {
          const container = document.getElementById(scannerContainerId);
          if (container) {
            let torchChecks = 0;
            const torchCheckInterval = setInterval(() => {
              torchChecks++;
              try {
                const videoEl = container.querySelector('video') as HTMLVideoElement | null;
                const stream = (videoEl && (videoEl as any).srcObject instanceof MediaStream) ? (videoEl as any).srcObject as MediaStream : null;
                const track = stream?.getVideoTracks()?.[0];
                const caps = track?.getCapabilities?.();
                if (track && track.readyState === 'live' && caps && Object.prototype.hasOwnProperty.call(caps, 'torch') && (caps as any).torch) {
                  setTorchSupported(true);
                  clearInterval(torchCheckInterval);
                  console.log('🔦 Torch capability detected');
                }
              } catch {}
              if (torchChecks > 50) clearInterval(torchCheckInterval);
            }, 120);
          }
        }, 1000);
      }

    } catch (error) {
      console.error("❌ Direct scanner initialization failed:", error);
      initializingRef.current = false;
      if (isComponentMounted) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        if (errorMsg.includes('No cameras available')) {
          setError("No cameras found on this device. Please ensure your device has a camera.");
        } else if (errorMsg.includes('NotAllowedError') || errorMsg.includes('Permission denied')) {
          setError("Camera access denied. Please enable camera permissions and try again.");
        } else if (errorMsg.includes('NotReadableError') || errorMsg.includes('Could not start video source')) {
          setError("Camera is busy or unavailable. Please close other apps using the camera and try again.");
        } else {
          setError("Failed to initialize camera. Please check your camera permissions and try again.");
        }
        setIsScanning(false);
      }
    }
  };

  // PHONEPE-STYLE: Start scanner immediately when component mounts
  useEffect(() => {
    const startScannerDirectly = async () => {
      if (!isComponentMounted) return;
      
      try {
        console.log('🚀 Starting PhonePe-style scanner directly...');
        
        // Clear vendor context first
        await clearVendorContext();
        
        // DIRECT APPROACH: Skip permission UI, go straight to camera
        console.log('📱 Checking camera access...');
        const status = await checkCameraPermission();
        
        if (status === 'granted' && isComponentMounted) {
          console.log('✅ Camera access confirmed, starting scanner immediately...');
          
          // Minimal delay for DOM readiness
          setTimeout(() => {
            if (isComponentMounted) {
              const containerElement = document.getElementById(scannerContainerId);
              if (containerElement) {
                console.log('🎯 Container ready, initializing scanner...');
                // Start scanner immediately
                initializeScanner().catch(error => {
                  console.error('❌ Scanner initialization failed:', error);
                  if (isComponentMounted) {
                    setError('Failed to start scanner. Please refresh and try again.');
                  }
                });
              } else {
                console.error('❌ Scanner container not found');
                if (isComponentMounted) {
                  setError('Scanner container not ready. Please refresh the page.');
                }
              }
            }
          }, 300); // Minimal delay, just for DOM
        } else {
          console.log('⚠️ Camera access not granted, status:', status);
          // Error already set in checkCameraPermission
        }
      } catch (error) {
        console.error("❌ Failed to start scanner directly:", error);
        if (isComponentMounted) {
          setError("Failed to start camera. Please refresh and try again.");
        }
      }
    };

    // Start immediately
    startScannerDirectly();
  }, [clearVendorContext, isComponentMounted]);

  const renderScannerContent = () => (
    <div 
      className="w-full max-w-lg mx-auto shadow-xl rounded-2xl p-6 md:p-8 space-y-6"
      style={{ backgroundColor: theme.background, color: theme.text }}
    >
      <div className="text-center">
        <h2 className="text-xl font-bold mb-2" style={{ color: theme?.primary }}>
          Scan QR Code
        </h2>
        <p className="text-sm mb-4" style={{ color: theme?.text }}>
          Position the QR code within the frame
        </p>
      </div>

      {hasNavigated || navigatedRef.current ? (
        <div className="w-full aspect-square max-w-xs mx-auto border-2 border-green-500 rounded-lg overflow-hidden flex flex-col items-center justify-center bg-green-50">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm text-center mb-3 px-4 text-green-700 font-medium">
            QR Code Detected!
          </p>
          <p className="text-xs text-center px-4 text-green-600">
            Navigating to menu...
          </p>
        </div>
      ) : isBlocked ? (
        <div className="w-full aspect-square max-w-xs mx-auto border-2 border-red-500 rounded-lg overflow-hidden flex flex-col items-center justify-center bg-red-50">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-sm text-center mb-3 px-4 text-red-700 font-medium">
            Scanner Blocked
          </p>
          <p className="text-xs text-center px-4 text-red-600 mb-3">
            Please reset to continue
          </p>
          <button
            onClick={resetScanner}
            className="py-2 px-4 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600"
          >
            Reset Scanner
          </button>
        </div>
      ) : permissionStatus !== 'granted' ? (
        <div className="w-full aspect-square max-w-xs mx-auto border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden flex flex-col items-center justify-center p-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-sm text-center mb-3 px-4" style={{ color: theme?.text }}>
            {permissionStatus === 'denied' ? 
              'Camera access was denied' : 
              'Camera access is required to scan QR codes'}
          </p>
          {permissionStatus === 'denied' && (
            <p className="text-xs text-center mb-3 px-4" style={{ color: theme?.text }}>
              Please enable camera permissions in your browser settings and try again
            </p>
          )}
          <button
            onClick={async () => {
              setError(null);
              setPermissionStatus('prompt');
              const status = await checkCameraPermission();
              if (status === 'granted') {
                setTimeout(() => {
                  if (isComponentMounted) {
                    initializeScanner();
                  }
                }, 500);
              }
            }}
            className="py-2 px-6 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: theme?.button?.primary?.bg || '#000000',
              color: theme?.button?.primary?.text || '#FFFFFF',
            }}
          >
            {permissionStatus === 'denied' ? '🔄 Try Again' : '📷 Enable Camera'}
          </button>
        </div>
      ) : (
        <div className="w-full max-w-md mx-auto relative">
          {/* Torch toggle overlay */}
          {torchSupported && isScanning && !hasNavigated && (
            <button
              onClick={async () => {
                try {
                  const container = document.getElementById(scannerContainerId);
                  const videoEl = container?.querySelector('video') as HTMLVideoElement | null;
                  const stream = (videoEl && (videoEl as any).srcObject instanceof MediaStream) ? (videoEl as any).srcObject as MediaStream : null;
                  const track = stream?.getVideoTracks()?.[0];
                  if (track && track.applyConstraints) {
                    const next = !torchOn;
                    await track.applyConstraints({ advanced: [{ torch: next }] as any });
                    setTorchOn(next);
                  }
                } catch {
                  setTorchSupported(false);
                }
              }}
              aria-label="Toggle torch"
              title="Torch"
              className="absolute z-10 top-2 right-2 w-10 h-10 rounded-full shadow bg-white/90 flex items-center justify-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2h12v4l-3 4v9a3 3 0 01-3 3 3 3 0 01-3-3V10L6 6V2z" />
                {torchOn ? <path d="M9 2h6" /> : null}
              </svg>
            </button>
          )}
          <div 
            id={scannerContainerId} 
            className="w-full" 
            style={{ display: (hasNavigated || navigatedRef.current) ? 'none' : 'block' }}
          />
          
          {/* Camera Toggle Button - PhonePe Style Flip */}
          <button
            onClick={async () => {
              try {
                console.log('🔄 Camera flip requested');
                
                // Get available cameras
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                
                if (videoDevices.length < 2) {
                  console.log('⚠️ Only one camera available, cannot flip');
                  return;
                }

                // Find current camera and switch to the other one
                let nextCameraId: string | undefined;
                
                if (preferredBackCameraIdRef.current) {
                  // Currently using back camera, switch to front
                  const frontCamera = videoDevices.find(device => {
                    const label = device.label.toLowerCase();
                    return (label.includes('front') || label.includes('user') || label.includes('facetime')) &&
                           device.deviceId !== preferredBackCameraIdRef.current;
                  });
                  
                  if (frontCamera) {
                    nextCameraId = frontCamera.deviceId;
                    console.log('📱 Switching to front camera:', frontCamera.label);
                  } else {
                    // Fallback to first camera that's not the current one
                    const otherCamera = videoDevices.find(device => 
                      device.deviceId !== preferredBackCameraIdRef.current
                    );
                    if (otherCamera) {
                      nextCameraId = otherCamera.deviceId;
                      console.log('📱 Switching to other camera:', otherCamera.label);
                    }
                  }
                } else {
                  // Currently using front camera, switch to back
                  const { cameraId } = await getBestAvailableCamera();
                  if (cameraId) {
                    nextCameraId = cameraId;
                    console.log('📱 Switching to back camera');
                  }
                }

                if (nextCameraId) {
                  // Stop current scanner
                  if (scannerRef.current) {
                    await scannerRef.current.stop();
                    scannerRef.current = null;
                  }
                  
                  // Update preferred camera
                  preferredBackCameraIdRef.current = nextCameraId;
                  
                  // Wait for camera to be released
                  await new Promise(resolve => setTimeout(resolve, 500));
                  
                  // Reinitialize scanner with new camera
                  await initializeScanner();
                } else {
                  console.log('⚠️ No suitable camera found for flip');
                }
              } catch (error) {
                console.error('❌ Error flipping camera:', error);
                // Try to reinitialize with original settings
                try {
                  await initializeScanner();
                } catch (reinitError) {
                  console.error('❌ Failed to reinitialize scanner:', reinitError);
                  setError('Failed to switch camera. Please try again.');
                }
              }
            }}
            aria-label="Switch camera"
            title="Switch camera"
            className="absolute bottom-3 right-3 md:bottom-4 md:right-10 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:shadow-xl z-50"
            style={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid #000000' /* basil-todo */
            }}
          >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2">
                {/* Simple refresh arrows - clear and distinct */}
                <path d="M17 2l4 4-4 4"/>
                <path d="M21 6H9a4 4 0 0 0-4 4v1"/>
                <path d="M7 22l-4-4 4-4"/>
                <path d="M3 18h12a4 4 0 0 0 4-4v-1"/>
              </svg>
          </button>
        </div>
      )}
      
      {/* No restart button during normal flow - shown only on error/stall below */}

      {/* Show prominent reset button when blocked */}
      {isBlocked && (
        <div className="flex justify-center mt-4">
          <button
            onClick={resetScanner}
            className="py-3 px-6 rounded-lg text-base font-bold bg-red-500 text-white hover:bg-red-600 shadow-lg"
          >
            🔄 Reset Scanner
          </button>
        </div>
      )}

      {/* Simple processing indicator - no complex recovery UI */}
      {isProcessingQR && (
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600">
            Redirecting to menu...
          </p>
        </div>
      )}

      {error && (
        <div className="space-y-3">
          <p className="p-3 text-sm rounded-lg" style={{ 
            backgroundColor: 'var(--status-bad-bg)', 
            color: 'var(--status-bad-text)',
            border: '1px solid #FFCDD2' /* basil-todo */
          }}>
            {error}
          </p>
          
          <div className="flex justify-center">
            <button
              onClick={resetScanner}
              className="py-2 px-4 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-600"
            >
              Reset Scanner
            </button>
          </div>
        </div>
      )}
      
      {/* Show reset button if scanner seems blocked (fallback) */}
      {isProcessingQR && !hasNavigated && !error && !isBlocked && (
        <div className="flex justify-center mt-4">
          <button
            onClick={resetScanner}
            className="py-2 px-4 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600"
          >
            Reset Scanner
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ backgroundColor: theme.background, color: theme.text }}>
      <CardLayoutTheme
        title="Scan QR Code"
        categories={[{ id: 1, name: 'QR Scanner' }]}
        renderItems={renderScannerContent}
        sidebarTitle="QR Scanner"
      />
    </div>
  );
};

export default Scanner;
